import { useState, useCallback } from 'react';
import { User } from 'firebase/auth';
import { useCases } from '../../infrastructure/di/container';

type TelegramAccountStep = 'phone' | 'otp' | 'password' | 'completed';

interface UseTelegramAccountReturn {
  phone: string;
  otpCode: string;
  password: string;
  loading: boolean;
  error: string | null;
  step: TelegramAccountStep;
  accountUuid: string | null;
  setPhone: (phone: string) => void;
  setOtpCode: (code: string) => void;
  setPassword: (password: string) => void;
  createAccount: () => Promise<void>;
  finishAuth: () => Promise<void>;
  clearError: () => void;
}

export function useTelegramAccount(user: User | undefined | null): UseTelegramAccountReturn {
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<TelegramAccountStep>('phone');
  const [accountUuid, setAccountUuid] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const createAccount = useCallback(async () => {
    if (!user || !phone) {
      setError('Phone number is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Create telegram account with phone
      const result = await useCases.createTelegramAccount().execute({
        phone: phone.trim(),
      });
      
      if (result && result.uuid) {
        setAccountUuid(result.uuid);
        setStep('otp');
        await useCases.startTelegramAccountAuth().execute(result.uuid);
      } else {
        setError('Failed to create telegram account');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to create telegram account');
    } finally {
      setLoading(false);
    }
  }, [user, phone]);


  const finishAuth = useCallback(async () => {
    if (!user || !accountUuid || !otpCode) {
      setError('Verification code is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await useCases.finishTelegramAccountAuth().execute(accountUuid, {
        code: otpCode.trim(),
        password: password.trim() || null,
      });
      
      setStep('completed');
    } catch (e: any) {
      const errorMessage = e?.message || 'Authentication failed';
      
      // Check if we need password for 2FA
      if (errorMessage.toLowerCase().includes('password') && step !== 'password') {
        setStep('password');
        setError('Two-factor authentication password required');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [user, accountUuid, otpCode, password, step]);

  return {
    phone,
    otpCode,
    password,
    loading,
    error,
    step,
    accountUuid,
    setPhone,
    setOtpCode,
    setPassword,
    createAccount,
  finishAuth,
    clearError,
  };
}
